import { Link } from "react-router-dom";
import { Instagram, Music2, Facebook, Send } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { useConsent } from "@/context/ConsentContext";
import { GA_ENABLED } from "@/lib/analytics";
import Logo from "@/components/Logo";

const SOCIALS = [
  { key: "instagram", label: "Instagram", href: "https://instagram.com/bayomag", Icon: Instagram },
  { key: "tiktok", label: "TikTok", href: "https://www.tiktok.com/@bayomag", Icon: Music2 },
  { key: "facebook", label: "Facebook", href: "https://facebook.com/bayomag", Icon: Facebook },
];

export default function Footer() {
  const { lang } = useLang();
  const t = useDict(lang);
  const { reset: resetConsent } = useConsent();
  return (
    <footer className="mt-32 border-t border-[color:rgba(0,16,48,0.1)] bg-[var(--bg-2)]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="mb-4"><Logo size="lg" /></div>
          <p className="text-sm text-[color:var(--ink-2)] max-w-sm leading-relaxed mb-6">{t.tagline}</p>
          <div className="flex flex-wrap gap-3">
            {SOCIALS.map(({ key, label, href, Icon }) => (
              <a
                key={key}
                href={href}
                aria-label={label}
                data-testid={`footer-social-${key}`}
                title={label}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-[color:rgba(0,16,48,0.18)] transition-colors hover:border-[color:var(--ink)]"
              >
                <Icon size={16} strokeWidth={1.5} />
              </a>
            ))}
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)] mt-3">
            Suivez-nous · @bayomag
          </p>
        </div>
        <div>
          <p className="eyebrow mb-4">Navigation</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/recipes" className="hover:underline">{t.nav.recipes}</Link></li>
            <li><Link to="/shop" className="hover:underline">{t.nav.shop}</Link></li>
            <li><Link to="/submit" className="hover:underline">{t.nav.submit}</Link></li>
            <li><Link to="/leaderboard" className="hover:underline">{t.leaderboard.title}</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-4">Communauté</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/referral" className="hover:underline">Parrainage</Link></li>
            <li><Link to="/legal/contact" className="hover:underline">Contact</Link></li>
            <li className="text-sm text-[color:var(--ink-2)] flex items-center gap-2 mt-3">
              <Send size={14} strokeWidth={1.5} />
              hello@bayo-mag.com
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[color:rgba(0,16,48,0.08)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[color:var(--ink-3)]">
          <Link to="/legal/mentions" className="hover:underline">Mentions légales</Link>
          <span aria-hidden="true">·</span>
          <Link to="/legal/cgv" className="hover:underline">CGV</Link>
          <span aria-hidden="true">·</span>
          <Link to="/legal/confidentialite" className="hover:underline">Confidentialité (RGPD)</Link>
          <span aria-hidden="true">·</span>
          <Link to="/legal/livraison" className="hover:underline">Livraison</Link>
          <span aria-hidden="true">·</span>
          <Link to="/legal/retours" className="hover:underline">Retours</Link>
          <span aria-hidden="true">·</span>
          <Link to="/legal/contact" className="hover:underline">Contact</Link>
          {GA_ENABLED && (
            <>
              <span aria-hidden="true">·</span>
              <button
                data-testid="footer-manage-cookies"
                onClick={resetConsent}
                className="hover:underline text-xs text-[color:var(--ink-3)]"
              >
                {lang === "fr" ? "Gérer les cookies" : "Manage cookies"}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="border-t border-[color:rgba(0,16,48,0.08)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-xs text-[color:var(--ink-3)]">{t.home.footer}</p>
          <p className="text-xs text-[color:var(--ink-3)]">{`Haïti · Caraïbes · Afrique de l'Ouest`}</p>
        </div>
      </div>
    </footer>
  );
}
